import { http } from "./http";
import { wpPostsEndpoint, baseURL, moderationEndpoint, mobileEndpoint } from "./wpConfig";
import { RESTPost } from "./types";
import { UserCredentials } from "./wpAuth";

interface PostsResponse {
    posts: RESTPost[];
    totalPages: number;
    totalPosts: number;
}

const parsePaginationHeaders = (headers: Headers) => ({
    totalPages: parseInt(headers.get("x-wp-totalpages") ?? "1", 10),
    totalPosts: parseInt(headers.get("x-wp-total") ?? "0", 10),
});

export const getLastWPPosts = async (page: number, authorId?: string): Promise<PostsResponse> => {
    const endPoint: string = `${baseURL}${wpPostsEndpoint}?page=${page}&per_page=10&categories_exclude=7615&with_comments=1${authorId ? `&author=${authorId}` : ''}`;
    const { data, headers } = await http.getWithHeaders<RESTPost[]>(endPoint);
    return { posts: data, ...parsePaginationHeaders(headers) };
};

export const getTrendingWPPosts = async (page: number): Promise<PostsResponse> => {
    const twentyfourHoursBefore: Date = new Date(new Date().getTime() - (24 * 60 * 60 * 1000));
    const endPoint: string = `${baseURL}${wpPostsEndpoint}?page=${page}&per_page=10&categories_exclude=7615&with_comments=1&after=${twentyfourHoursBefore.toISOString()}&orderby=ratings_score&order=desc`;
    const { data, headers } = await http.getWithHeaders<RESTPost[]>(endPoint);
    return { posts: data, ...parsePaginationHeaders(headers) };
};

export const getTrendingWPPostsThisMonth = async (page: number): Promise<PostsResponse> => {
    const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const endPoint: string = `${baseURL}${wpPostsEndpoint}?page=${page}&per_page=10&categories_exclude=7615&with_comments=1&after=${firstDayOfMonth.toISOString()}&orderby=ratings_score&order=desc`;
    const { data, headers } = await http.getWithHeaders<RESTPost[]>(endPoint);
    return { posts: data, ...parsePaginationHeaders(headers) };
};

export const getTrendingWPPostsThisYear = async (page: number): Promise<PostsResponse> => {
    const firstDayOfYear = new Date(new Date().getFullYear(), 0, 1);
    const endPoint: string = `${baseURL}${wpPostsEndpoint}?page=${page}&per_page=10&categories_exclude=7615&with_comments=1&after=${firstDayOfYear.toISOString()}&orderby=ratings_score&order=desc`;
    const { data, headers } = await http.getWithHeaders<RESTPost[]>(endPoint);
    return { posts: data, ...parsePaginationHeaders(headers) };
};

export const getVerbisWPPosts = async (page: number): Promise<PostsResponse> => {
    const endPoint: string = `${baseURL}${wpPostsEndpoint}?page=${page}&per_page=10&categories=7615&with_comments=1&with_verbi=1`;
    const { data, headers } = await http.getWithHeaders<RESTPost[]>(endPoint);
    return { posts: data, ...parsePaginationHeaders(headers) };
};

export const getTrashedPosts = async (userCredentials: UserCredentials, page: number): Promise<PostsResponse> => {
    const endPoint: string = `${baseURL}${moderationEndpoint}/trash?page=${page}&per_page=10&with_comments=1&with_verbi=1`;
    const { data, headers } = await http.getWithHeaders<RESTPost[]>(endPoint, { auth: userCredentials });
    return { posts: data, ...parsePaginationHeaders(headers) };
};

export const getFacebookVotedPosts = async (page: number): Promise<PostsResponse> => {
    const endPoint: string = `${baseURL}${wpPostsEndpoint}?page=${page}&per_page=10&has_facebook_votes=1&with_comments=1&with_verbi=1`;
    const { data, headers } = await http.getWithHeaders<RESTPost[]>(endPoint);
    return { posts: data, ...parsePaginationHeaders(headers) };
};

export const getPostsWithCorrections = async (page: number): Promise<PostsResponse> => {
    const endPoint: string = `${baseURL}${wpPostsEndpoint}?page=${page}&per_page=10&has_correction=1&with_comments=1&with_verbi=1`;
    const { data, headers } = await http.getWithHeaders<RESTPost[]>(endPoint);
    return { posts: data, ...parsePaginationHeaders(headers) };
};

export const ratePost = (userCredentials: UserCredentials, postId: number): Promise<{ success: boolean, new_ratings_score: number }> => {
    const endPoint: string = `${baseURL}${mobileEndpoint}/ratePost/${postId}`;
    return http.get<{ success: boolean, new_ratings_score: number }>(endPoint, { auth: userCredentials });
};

export const trashPost = (userCredentials: UserCredentials, postId: number): Promise<RESTPost> => {
    const endPoint: string = `${baseURL}${wpPostsEndpoint}/${postId}`;
    return http.del<RESTPost>(endPoint, { auth: userCredentials });
};

export const restorePost = (userCredentials: UserCredentials, postId: number): Promise<RESTPost> => {
    const endPoint: string = `${baseURL}${wpPostsEndpoint}/${postId}`;
    return http.post<RESTPost>(endPoint, { status: 'publish' }, { auth: userCredentials });
};

export const getPostById = (userCredentials: UserCredentials, postId: number): Promise<RESTPost> => {
    const endPoint: string = `${baseURL}${wpPostsEndpoint}/${postId}?with_comments=1&with_verbi=1`;
    return http.get<RESTPost>(endPoint, { auth: userCredentials });
};

export const getPostByIdForEdit = (userCredentials: UserCredentials, postId: number): Promise<RESTPost> => {
    const endPoint: string = `${baseURL}${mobileEndpoint}/post/${postId}?with_verbi=1`;
    return http.get<RESTPost>(endPoint, { auth: userCredentials });
};

export const searchWPPosts = async (searchTerm: string, page: number, searchTranscripts: boolean = false): Promise<PostsResponse> => {
    const transcriptsParam = searchTranscripts ? '&search_transcripts=1' : '';
    const endPoint: string = `${baseURL}${wpPostsEndpoint}?search=${encodeURIComponent(searchTerm)}&page=${page}&per_page=10&categories_exclude=7615&with_comments=1${transcriptsParam}`;
    const { data, headers } = await http.getWithHeaders<RESTPost[]>(endPoint);
    return { posts: data, ...parsePaginationHeaders(headers) };
};